import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from './Button'

type FallbackRender = (args: { error: Error; reset: () => void }) => ReactNode

type RouteErrorBoundaryProps = {
  children: ReactNode
  /** Optional render prop for a custom fallback. Receives the caught error and a reset callback. */
  fallback?: FallbackRender
  /**
   * Optional reporting hook invoked when an error is caught. Stub for future
   * Sentry integration; defaults to logging to the console.
   */
  onError?: (error: Error, info: ErrorInfo) => void
}

type RouteErrorBoundaryState = {
  error: Error | null
}

/**
 * Catches render-time throws in its subtree and renders a friendly fallback
 * instead of white-screening the SPA. Generic and reusable; wire it into the
 * route tree separately.
 */
export class RouteErrorBoundary extends Component<
  RouteErrorBoundaryProps,
  RouteErrorBoundaryState
> {
  state: RouteErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (this.props.onError) {
      this.props.onError(error, info)
    } else {
      console.error('[RouteErrorBoundary] Uncaught render error:', error, info)
    }
  }

  reset = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state

    if (error) {
      if (this.props.fallback) {
        return this.props.fallback({ error, reset: this.reset })
      }

      return (
        <div
          role="alert"
          className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center"
        >
          <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
          <p className="max-w-md text-sm text-gray-600">
            An unexpected error occurred while rendering this page. You can try again, or reload the
            page if the problem persists.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={this.reset}>
              Try again
            </Button>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
